import type { Carer } from '@lmc/ts-common'
import { type CSSProperties, Suspense } from 'react'
import { getNominative, sortList } from '../../../../common/utils'
import Field from '../../../fields'
import { ResidentSelectOption } from '../../../generic'

interface ResidentPrimaryInfoProps {
  data: {
    carers: Carer[]
    features: any
    zones: any[]
  }
  fields: any
  formId: string
  preferredPeopleNominative: string
  profile: any
  user: {
    role: string
  }
}

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'hospitalised', label: 'Hospitalised' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'discharged', label: 'Discharged' }
]

const styles = {
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  formField: {
    paddingTop: 20
  }
}

export default function ResidentPrimaryInfo({
  data: { carers, features, zones },
  fields,
  formId,
  preferredPeopleNominative,
  profile,
  user
}: ResidentPrimaryInfoProps) {
  const isDummy = profile?.status === 'dummy'

  const isCareHomeAdmin = user?.role === 'carehome-admin'

  return (
    <div style={styles.fieldsContainer as CSSProperties}>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys="preferredName"
            label="Preferred name"
            formId={formId}
            type="text"
            values={fields}
            disabled={isDummy}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys={['name.first', 'name.last']}
            label="Name"
            formId={formId}
            placeholders={['First name', 'Last name']}
            required
            type="multiText"
            values={fields}
            disabled={isDummy}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            className="gender-field"
            keys="gender"
            label="Gender"
            formId={formId}
            options={[{ value: 'male' }, { value: 'female' }]}
            required
            type="radios"
            values={fields}
            disabled={isDummy}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            className="birthdate-field"
            keys="dateOfBirth"
            label="Date of birth"
            formId={formId}
            required
            type="birthdate"
            validationTypes="birthdate"
            values={fields}
            disabled={isDummy}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys="status"
            label="Status"
            formId={formId}
            options={STATUSES}
            required
            type="select"
            values={fields}
            disabled={isDummy && isCareHomeAdmin}
            placeholder={isDummy && isCareHomeAdmin ? 'Example' : undefined}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys="zone"
            label="Zone"
            formId={formId}
            features={features}
            type="zone"
            values={fields}
            zones={zones}
          />
        </Suspense>
      </div>
      <div style={styles.formField}>
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            keys="assignAll"
            label="Assign to all carers"
            note={getCarersNote(preferredPeopleNominative)}
            formId={formId}
            type="switch"
            values={fields}
          />
        </Suspense>
      </div>
      {fields.assignAll === false && (
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formatOptionLabel={ResidentSelectOption}
              keys="carers"
              label="Carers"
              formId={formId}
              // @ts-ignore
              options={sortList(carers, { active: true })}
              required
              type="multiSelect"
              values={fields}
            />
          </Suspense>
        </div>
      )}
    </div>
  )
}

const getCarersNote = (preferredPeopleNominative: string) =>
  `Any tasks assigned to this ${getNominative(
    preferredPeopleNominative,
    'resident'
  )} will be seen by all carers`
